import { createContext, useReducer, useContext, useState, useEffect } from 'react';
import { useSafeLayoutEffect } from './useSafeLayoutEffect.js';

/** Chakra, MIT https://github.com/chakra-ui/chakra-ui/blob/38acfe89c5d1f1edc67bbc44e2edd38980ca3e08/packages/components/media-query/src/use-media-query.ts#L17 */
const EnvironmentContext = /*#__PURE__*/createContext({
  getDocument() {
    return document;
  },
  getWindow() {
    return window;
  }
});
EnvironmentContext.displayName = 'EnvironmentContext';
function useEnvironment({
  defer
} = {}) {
  const [, forceUpdate] = useReducer(c => c + 1, 0);
  useSafeLayoutEffect(() => {
    if (!defer) return;
    forceUpdate();
  }, [defer]);
  return useContext(EnvironmentContext);
}
/**
 * React hook that tracks state of a CSS media query
 *
 * @param query the media query to match
 * @param options the media query options { fallback, ssr }
 *
 * @see https://github.com/chakra-ui/chakra-ui/blob/38acfe89c5d1f1edc67bbc44e2edd38980ca3e08/packages/components/media-query/src/use-media-query.ts#L17
 */
function useMediaQuery(query, options = {}) {
  const {
    ssr = true,
    fallback
  } = options;
  const {
    getWindow
  } = useEnvironment();
  const queries = Array.isArray(query) ? query : [query];
  let fallbackValues = Array.isArray(fallback) ? fallback : [fallback];
  fallbackValues = fallbackValues.filter(v => v != null);
  const [value, setValue] = useState(() => {
    return queries.map((query, index) => ({
      media: query,
      matches: ssr ? !!fallbackValues[index] : getWindow().matchMedia(query).matches
    }));
  });
  useEffect(() => {
    const win = getWindow();
    setValue(queries.map(query => ({
      media: query,
      matches: win.matchMedia(query).matches
    })));
    const mql = queries.map(query => win.matchMedia(query));
    const handler = evt => {
      setValue(prev => {
        return prev.slice().map(item => {
          if (item.media === evt.media) return {
            ...item,
            matches: evt.matches
          };
          return item;
        });
      });
    };
    mql.forEach(mql => {
      if (typeof mql.addListener === 'function') {
        mql.addListener(handler);
      } else {
        mql.addEventListener('change', handler);
      }
    });
    return () => {
      mql.forEach(mql => {
        if (typeof mql.removeListener === 'function') {
          mql.removeListener(handler);
        } else {
          mql.removeEventListener('change', handler);
        }
      });
    };
  }, [getWindow]);
  return value.map(item => item.matches);
}

export { useEnvironment, useMediaQuery };
